import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getSeasonForLeague } from "@/lib/seasonUtils";
import { useTranslation } from "react-i18next";

export interface LeagueStandings {
  league_id: string;
  league_name: string;
  league_logo?: string;
  country: string;
  api_football_id: number;
  last_updated: string | null;
  has_data: boolean;
}

export function useAllLeaguesStandings() {
  const [leaguesStandings, setLeaguesStandings] = useState<LeagueStandings[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation('errors');

  useEffect(() => {
    fetchAllLeaguesStandings();
  }, []);

  // Real-time updates now handled by unified channel in AppLayout (useRealtimeSync)

  const fetchAllLeaguesStandings = async () => {
    try {
      setLoading(true);

      // Fetch all leagues with their standings status
      const { data: leagues, error: leaguesError } = await supabase
        .from('leagues')
        .select('id, name, country, logo_url, api_football_id')
        .order('name');

      if (leaguesError) {
        console.error('Error fetching leagues:', leaguesError);
        toast({
          title: t('error'),
          description: t('loadingGames'),
          variant: "destructive"
        });
        return;
      }

      if (!leagues) return;

      // Check standings_cache for each league (using dynamic season per league)
      const leagueStandingsPromises = leagues.map(async (league) => {
        const season = getSeasonForLeague(league.api_football_id);
        const { data: standingsCache } = await supabase
          .from('standings_cache')
          .select('updated_at')
          .eq('league_id', league.id)
          .eq('season', season)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        return {
          league_id: league.id,
          league_name: league.name,
          league_logo: league.logo_url,
          country: league.country,
          api_football_id: league.api_football_id,
          last_updated: standingsCache?.updated_at || null,
          has_data: !!standingsCache
        };
      });

      const results = await Promise.all(leagueStandingsPromises);
      setLeaguesStandings(results);

    } catch (error) {
      console.error('Error in fetchAllLeaguesStandings:', error);
      toast({
        title: t('error'),
        description: t('unexpectedLoadingGames'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    fetchAllLeaguesStandings();
  };

  return {
    leaguesStandings,
    loading,
    refresh,
    refetch: fetchAllLeaguesStandings
  };
}